import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, KanbanSquare, Users, Building2, FolderKanban, CheckSquare,
  Settings, LogOut, Bell, Menu, X, UserCog, CalendarDays, FileText, Receipt, BarChart3, Percent, Clapperboard,
} from "lucide-react";
import { CrumbCtx, Breadcrumbs } from "../../components/crumbs";
import { useAuth } from "../../context/AuthContext";
import { Logo, LogoMark } from "../../components/Logo";
import { useNotifications } from "../../components/crm";
import { AnimatePresence, motion } from "framer-motion";
import { formatDistanceToNow } from "date-fns";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/leads", label: "Pipeline", icon: KanbanSquare },
  { to: "/admin/projects", label: "Projects", icon: FolderKanban },
  { to: "/admin/quotes", label: "Quotes", icon: FileText },
  { to: "/admin/invoices", label: "Invoices", icon: Receipt },
  { to: "/admin/reports", label: "Reporting", icon: BarChart3 },
  { to: "/admin/promotions", label: "Promotions", icon: Percent },
  { to: "/admin/post", label: "Post queue", icon: Clapperboard },
  { to: "/admin/contacts", label: "Contacts", icon: Users },
  { to: "/admin/companies", label: "Companies", icon: Building2 },
  { to: "/admin/tasks", label: "Tasks", icon: CheckSquare },
  { to: "/admin/availability", label: "Availability", icon: CalendarDays },
  { to: "/admin/cms", label: "Website CMS", icon: Settings },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileNav, setMobileNav] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [names, setNames] = useState({});
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0 }); }, [pathname]);
  const projId = pathname.match(/^\/admin\/projects\/([^/]+)/)?.[1];
  const salesRec = projId && ["New", "Contacted", "Quote sent", "Lost"].includes(names[projId]?.stage);
  const crumbs = useMemo(() => ({ names, set: (id, v) => setNames((n) => (n[id]?.name === v.name && n[id]?.stage === v.stage ? n : { ...n, [id]: v })) }), [names]);
  const { items, unread, markAll } = useNotifications();
  const isAdmin = user?.permissions_effective?.manage_roles;

  const doLogout = () => { logout(); navigate("/login"); };

  const base = NAV.filter((n) => n.to !== "/admin/cms" || user?.permissions_effective?.manage_cms);
  const nav = isAdmin ? [...base, { to: "/admin/users", label: "Team", icon: UserCog }] : base;

  const SidebarInner = (
    <>
      <div className="px-5 py-5"><Logo dark /></div>
      <nav className="flex-1 px-3 space-y-1">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setMobileNav(false)}
            className={({ isActive }) => {
              const on = salesRec ? n.to === "/admin/leads" : isActive;
              return `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                on ? "bg-brand-purple text-white" : "text-white/60 hover:text-white hover:bg-white/5"
              }`;
            }}
            data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
          >
            <n.icon size={18} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="px-3.5 py-2 text-white/50 text-xs">
          <div className="text-white font-medium text-sm">{user?.name}</div>
          <div className="capitalize">{user?.role}</div>
        </div>
        <button onClick={doLogout} className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium text-white/60 hover:text-white hover:bg-white/5" data-testid="admin-logout">
          <LogOut size={18} /> Log out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFC] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-brand-purpleDeep flex-col fixed inset-y-0 z-30">{SidebarInner}</aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileNav && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setMobileNav(false)} />
            <motion.aside initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }} className="fixed inset-y-0 left-0 w-64 bg-brand-purpleDeep flex flex-col z-50 lg:hidden">{SidebarInner}</motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="flex-1 lg:ml-64 min-w-0">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-border flex items-center justify-between px-5 sticky top-0 z-20">
          <button className="lg:hidden text-brand-purpleDeep" onClick={() => setMobileNav(true)} data-testid="admin-mobile-menu"><Menu /></button>
          <div className="hidden lg:block font-heading font-bold text-brand-purpleDeep">EasyVideo CRM</div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-brand-purpleDeep/50 hover:text-brand-purple hidden sm:block">View website ↗</Link>
            <div className="relative">
              <button onClick={() => setBellOpen((v) => !v)} className="relative p-2 rounded-lg hover:bg-brand-light text-brand-purpleDeep" data-testid="notif-bell">
                <Bell size={20} />
                {unread > 0 && <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-brand-orange text-white text-[10px] flex items-center justify-center">{unread}</span>}
              </button>
              {bellOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-border overflow-hidden z-50" data-testid="notif-panel">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <span className="font-heading font-bold text-sm text-brand-purpleDeep">Notifications</span>
                    <button onClick={markAll} className="text-xs text-brand-purple font-medium" data-testid="notif-mark-all">Mark all read</button>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {items.length === 0 && <div className="p-6 text-center text-sm text-brand-purpleDeep/40">No notifications</div>}
                    {items.map((n) => (
                      <div key={n.id} className={`px-4 py-3 border-b border-border last:border-0 ${!n.read ? "bg-brand-light/40" : ""}`}>
                        <div className="text-sm font-medium text-brand-purpleDeep">{n.title}</div>
                        {n.body && <div className="text-xs text-brand-purpleDeep/50 mt-0.5">{n.body}</div>}
                        <div className="text-[11px] text-brand-purpleDeep/40 mt-1">{n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true }) : ""}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="w-9 h-9 rounded-full bg-brand-purple text-white flex items-center justify-center font-heading font-bold text-sm">
              {user?.name?.[0] || "U"}
            </div>
          </div>
        </header>

        <div className="p-5 sm:p-8">
          <CrumbCtx.Provider value={crumbs}>
            <Breadcrumbs />
            <Outlet />
          </CrumbCtx.Provider>
        </div>
      </div>
    </div>
  );
}
